/**
 * Central data hook for the Clinical Day View dashboard.
 *
 * Today, Patients, and patient quick search share one compact patient directory.
 * Document-derived values come from patients/{id}._summary and never require a
 * list screen to download document subcollections.
 */
import { useMemo } from "react";
import { useAuth } from "../utils/authContext";
import { usePatientDirectory } from "./usePatientDirectory";

export function useDashboardData() {
    const { currentUser } = useAuth();
    const userId = currentUser?.uid;
    const {
        patients: allPatients,
        hasLoaded,
        isLoading,
        isRefreshing,
        error,
        refresh,
    } = usePatientDirectory(userId);

    const pinnedPatients = allPatients.filter((patient) => patient.pin === true);
    const attentionItems = useMemo(() => computeAttentionItems(allPatients), [allPatients]);

    return {
        allPatients,
        hasLoaded,
        pinnedPatients,
        attentionItems,
        isLoading,
        isRefreshing,
        error: error?.message || null,
        refetch: refresh,
    };
}

function computeAttentionItems(patients) {
    const items = [];
    const staleThresholdMs = 10 * 60 * 1000;
    const now = Date.now();

    for (const patient of patients) {
        const name = `${patient.lastName}, ${patient.firstName}`;
        for (const summaryItem of patient.attentionItems || []) {
            if (summaryItem.type === "error") {
                items.push({
                    type: "error",
                    severity: "high",
                    label: `${name}: document processing error`,
                    patientId: patient.id,
                    patientName: name,
                    documentId: summaryItem.documentId,
                });
            } else if (
                summaryItem.type === "stale"
                || (
                    summaryItem.type === "processing"
                    && summaryItem.startedAt
                    && now - summaryItem.startedAt.getTime() > staleThresholdMs
                )
            ) {
                items.push({
                    type: "stale",
                    severity: "medium",
                    label: `${name}: processing stuck (>10 min)`,
                    patientId: patient.id,
                    patientName: name,
                    documentId: summaryItem.documentId,
                });
            }
        }

        if (patient.document?.risk_assessment?.riskLevel === "significant") {
            items.push({
                type: "risk",
                severity: "high",
                label: `${name}: significant risk level`,
                patientId: patient.id,
                patientName: name,
            });
        }

        if (patient.overallSeverity?.trend === "getting worse") {
            items.push({
                type: "worsening",
                severity: "medium",
                label: `${name}: severity worsening`,
                patientId: patient.id,
                patientName: name,
            });
        }
    }

    const order = { high: 0, medium: 1, low: 2 };
    items.sort((a, b) => (order[a.severity] ?? 2) - (order[b.severity] ?? 2));
    return items;
}
